import Dexie, { type EntityTable } from 'dexie';
import { SUPPORTED_CURRENCIES } from '@bbpos/types';
import type { CurrencyCode } from '@bbpos/types';
import {
  PLAINTEXT_FIELDS,
  ENC_COLUMN,
  isEncryptedTable,
} from './encryption-config';

// ─── Sync Queue Entry ────────────────────────────────────────────────────────

// G2.22: BILL_ITEM removed — bills are pushed as atomic entities with items
// embedded in the BILL payload. Backend rejects BILL_ITEM at the DTO level.
export type SyncEntityType =
  | 'BILL'
  | 'STOCK_ADJUSTMENT'
  | 'PRODUCT'
  | 'EXPENSE'
  | 'CUSTOMER'
  | 'QUOTATION';

export type SyncOperation = 'CREATE' | 'UPDATE' | 'DELETE';

/**
 * SYNCED (T19): a BILL row the server took. It stays 24 h so the cashier's
 * offline-bills list shows its final number; every other entity type is
 * removed as soon as it syncs.
 */
export type SyncQueueStatus = 'PENDING' | 'PROCESSING' | 'FAILED' | 'CONFLICT' | 'DEAD' | 'SYNCED';

export interface PendingSyncEntry {
  id?: number;
  client_id: string;
  entity_type: SyncEntityType;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  device_id: string;
  status: SyncQueueStatus;
  created_at: string;
  retry_count: number;
  last_error: string | null;
  /** T19: who the bill was for, as the cashier saw it (local only, never pushed). */
  customer_label?: string | null;
  /** T19: the synced bill's server id and number (INV…), set when it syncs. */
  server_id?: string | null;
  server_bill_number?: string | null;
  /** T19: when it synced; the row is dropped 24 h later. */
  synced_at?: string | null;
}

// ─── Local Product Cache ─────────────────────────────────────────────────────

export interface CachedProduct {
  id: string;
  tenant_id: string;
  location_id: string;
  name: string;
  sku: string;
  barcode: string | null;
  price: number;
  cost: number;
  gst_rate: number;
  hsn_code: string | null;
  stock_quantity: number;
  category: string | null;
  unit: string;
  is_active: boolean;
  /** Owner-set per-module hide flags (inherited from the parent product). Default false. */
  hide_in_pos?: boolean;
  hide_in_sales?: boolean;
  /** A variant parent's lowest / highest variant price (not indexed — no schema bump). */
  variant_price_min?: number | null;
  variant_price_max?: number | null;
  synced_at: string;
}

// ─── Local Customer Cache ────────────────────────────────────────────────────

export interface CachedCustomer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  loyalty_points: number;
  synced_at: string;
  // Kept by the detail page for offline viewing/editing (sync plan Phase 4).
  // Not indexed, so no Dexie schema bump.
  date_of_birth?: string | null;
  anniversary?: string | null;
  address?: string | null;
  notes?: string | null;
  tags?: string[];
  /** The server version an offline edit starts from. */
  version?: number;
}

// ─── Draft Bills (cart persistence / crash recovery) ─────────────────────────

export interface DraftBillItem {
  id: string;
  productId: string;
  name: string;
  sku: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
  gstRate: number;
  hsnCode?: string;
  /**
   * Catalogue price before add-on surcharges (gst-parked 120): an offline
   * park uploads the surcharge (unitPrice − listPrice). Absent on older rows.
   */
  listPrice?: number;
  discountType: 'PERCENTAGE' | 'FLAT' | null;
  discountValue: number;
}

/**
 * Bill-level discount stored in IndexedDB. camelCase because this is a
 * purely client-side structure with no server DTO to mirror — unlike the
 * snake_case fields below that mirror server payloads.
 */
export interface DraftBillDiscount {
  type: 'PERCENTAGE' | 'FLAT';
  /** Paise for FLAT, whole percent 0..100 for PERCENTAGE */
  value: number;
  reason: string;
  appliedBy: string;
  appliedAt: number;
  voucherCode?: string;
  /** C03: a scoped voucher's matching products (see BillDiscount). */
  eligibleProductIds?: string[];
  scopeLabel?: string;
}

/** FR-BILL-050: pending discount approval state persisted in IndexedDB. */
export interface DraftPendingDiscountApproval {
  requestId: string;
  type: 'PERCENTAGE' | 'FLAT';
  value: number;
  reason: string;
  requestedAt: number;
}

export interface DraftBill {
  client_id: string;
  location_id: string | null;
  items: DraftBillItem[];
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  bill_mode: string;
  /** The split the cart showed; restore reads `supply_override`, not this. */
  is_inter_state: boolean;
  /**
   * gst-parked D7: the cashier's IGST switch. Absent on a draft saved before
   * task 080 — it resumes on the store's own split.
   */
  supply_override?: 'INTRA' | 'INTER' | null;
  /** Stored as 0/1 instead of boolean for Dexie indexing compatibility */
  is_parked: number;
  parked_reference: string | null;
  /** Optional bill-level discount carried through park/resume. */
  bill_discount?: DraftBillDiscount | null;
  /** FR-BILL-050: pending discount approval carried through park/resume. */
  pending_discount_approval?: DraftPendingDiscountApproval | null;
  /**
   * Server DRAFT bill id (numbered) when the cart was resumed from one — or,
   * for an offline park (gst-parked 120), once its upload created the draft.
   */
  server_bill_id?: string | null;
  /** gst-parked 120: who parked it on this till (inside the encrypted payload). */
  user_id?: string | null;
  /**
   * gst-parked 120: why the server refused this parked bill's upload (e.g. a
   * product no longer sold). It stays on the till until resumed or discarded.
   */
  upload_error?: string | null;
  /**
   * GP2-001: the total the cart showed when parked (promotion previewed,
   * cash-rounded) — the parked list shows it. Absent on older drafts.
   */
  parked_total?: number;
  created_at: string;
  updated_at: string;
}

// ─── Local Quotes (offline quotation creation, FR-BILL-033, FR-SYNC-006) ────
//
// Mirrors the server `Quote` shape (snake_case) plus client metadata:
//   - client_id is the offline UUID; becomes the sync key.
//   - server_id is null until the queued QUOTATION/CREATE entry is acknowledged
//     by the backend (handled by a future sync.consumer.ts case).
//   - is_synced is 0|1 (Dexie indexable) instead of boolean.
//
// All currency in paise (BIGINT-equivalent). All GST math uses Math.floor()
// per line, matching offline-billing.service.ts and the server billing engine.

export interface LocalQuoteItem {
  id: string;
  product_id: string;
  product_name: string;
  sku: string;
  unit: string;
  quantity: number;
  unit_price: number;
  unit_cost: number;
  gst_rate: number;
  hsn_code: string | null;
  discount: number;
  line_subtotal: number;
  line_tax: number;
  line_total: number;
}

export type LocalQuoteStatus =
  | 'ACTIVE'
  | 'APPROVED'
  | 'CONVERTED'
  | 'EXPIRED'
  | 'CANCELLED';

export interface LocalQuote {
  client_id: string;
  server_id: string | null;
  tenant_id: string;
  location_id: string;
  quote_number: string;
  status: LocalQuoteStatus;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: LocalQuoteItem[];
  subtotal: number;
  tax_total: number;
  discount_total: number;
  grand_total: number;
  is_inter_state: boolean;
  valid_until: string;
  notes: string | null;
  /** 0 | 1 (Dexie indexable) */
  is_synced: number;
  created_at: string;
  updated_at: string;
}

// ─── Sync Metadata (cursors per entity type) ─────────────────────────────────

export interface SyncMetadata {
  entity_type: string;
  last_synced_at: string;
}

// ─── App Config (offline tenant currency cache) ──────────────────────────────
//
// Multi-currency: the POS must know the tenant's immutable currency even
// when offline (page reload with no network → `/auth/me` fails → the
// in-memory auth profile is empty). This singleton row caches that currency
// so offline display/rounding inputs are correct. It is the offline mirror
// of the (server-immutable) `tenants.default_currency` — there is no FX and
// no per-bill currency, currency is one immutable value per tenant.
//
// Intentionally NOT in `TABLES_WITH_ENCRYPTION`: an ISO currency code is not
// PII or a business secret (same rationale as `sync_metadata`).

/** Fixed primary key for the singleton tenant-config row. */
export const TENANT_CONFIG_KEY = 'tenant';

/** Offline default — keeps every existing INR device byte-identical. */
export const DEFAULT_CURRENCY: CurrencyCode = 'INR';

export interface AppConfig {
  /** Singleton primary key — always {@link TENANT_CONFIG_KEY}. */
  key: string;
  /** The tenant's immutable display/rounding currency. */
  currency: CurrencyCode;
  /** Tenant the cached currency belongs to (null for the seeded default). */
  tenant_id: string | null;
  /** Owner display setting `show_tax_inclusive` (non-indexed; absent on old rows). */
  tax_inclusive?: boolean;
  /**
   * The signed-in user's configured discount cap, from the auth profile.
   * Cached so a till that boots offline still shows the tenant's own limit
   * rather than reverting to the shipped default. Absent on old rows.
   */
  discount_cap?: { percent: number; flat: number | null };
  updated_at: string;
}

/**
 * Narrow an arbitrary string (server contract types `default_currency` as
 * `string`) to a supported {@link CurrencyCode}. Unknown / empty / legacy
 * values fall back to `'INR'` — correct for every existing tenant.
 */
function normalizeCurrency(value: string | null | undefined): CurrencyCode {
  const code = (value ?? '').toUpperCase();
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code)
    ? (code as CurrencyCode)
    : DEFAULT_CURRENCY;
}

// ─── Database ────────────────────────────────────────────────────────────────

const DEVICE_ID_KEY = 'bbpos_device_id';

function getDeviceId(): string {
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch {
    // localStorage unavailable (Safari private mode, storage quota exceeded)
    // Fall back to a session-scoped ID
    return crypto.randomUUID();
  }
}

export const deviceId = getDeviceId();

class PosDatabase extends Dexie {
  pending_sync!: EntityTable<PendingSyncEntry, 'id'>;
  products!: EntityTable<CachedProduct, 'id'>;
  customers!: EntityTable<CachedCustomer, 'id'>;
  draft_bills!: EntityTable<DraftBill, 'client_id'>;
  local_quotes!: EntityTable<LocalQuote, 'client_id'>;
  sync_metadata!: EntityTable<SyncMetadata, 'entity_type'>;
  app_config!: EntityTable<AppConfig, 'key'>;

  constructor() {
    super('bbpos');

    this.version(1).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      sync_metadata: 'entity_type',
    });

    // v2: add tenant_id + location_id to enforce multi-tenant isolation.
    // Stale records (lacking these fields) are purged — re-synced on next online pull.
    this.version(2)
      .stores({
        pending_sync: '++id, client_id, entity_type, status, created_at',
        products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id]',
        customers: 'id, phone, email, synced_at',
        draft_bills: 'client_id, is_parked, created_at',
        sync_metadata: 'entity_type',
      })
      .upgrade((tx) => tx.table('products').clear());

    // v3: scoped barcode and category compound indexes for direct indexed lookups.
    // No upgrade() needed — all rows already have tenant_id + location_id from v2.
    this.version(3).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      sync_metadata: 'entity_type',
    });

    // v4: add local_quotes table for offline quotation creation (G2.15).
    // FR-BILL-033, FR-SYNC-006 (idempotent upsert via client_id).
    // No data migration — new table only.
    this.version(4).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      local_quotes: 'client_id, server_id, status, is_synced, [tenant_id+location_id], created_at',
      sync_metadata: 'entity_type',
    });

    // v5: G2.27 — at-rest encryption. Schema is unchanged (the encrypted
    // payload is stashed in an unindexed `_enc` column that Dexie does not
    // need to know about — it's just a regular property). The upgrade hook
    // is a no-op for legacy rows: we defer re-encryption to the next write
    // of each row (through the service-layer helpers). Stale plaintext
    // poses no worse risk than the pre-v5 state and the deferred path
    // avoids blocking app startup on a remote KEK fetch.
    //
    // Encryption happens at the service layer via `encrypted-access.ts`
    // rather than in a Dexie middleware because DBCore middleware cannot
    // `await` WebCrypto inside an IDB transaction without triggering
    // `InvalidStateError` — IDB auto-closes the transaction on microtask
    // yield. See `encrypted-access.ts` header for the full rationale.
    this.version(5).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      local_quotes: 'client_id, server_id, status, is_synced, [tenant_id+location_id], created_at',
      sync_metadata: 'entity_type',
    });

    // v6: multi-currency — add the `app_config` singleton table caching the
    // tenant's immutable currency for offline display/rounding inputs.
    // Existing tables are unchanged (re-declared verbatim, as Dexie requires).
    //
    // The upgrade callback seeds a default `'INR'` config row so a device
    // that upgrades while offline keeps byte-identical INR behaviour until
    // the next online `/auth/me` re-stamps the real tenant currency via
    // `cacheTenantCurrency()`. No other table holds currency, so there are
    // no pre-existing rows to backfill — INR remains the read-time default
    // everywhere (`getCachedTenantCurrency()` / `normalizeCurrency()`).
    this.version(6)
      .stores({
        pending_sync: '++id, client_id, entity_type, status, created_at',
        products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
        customers: 'id, phone, email, synced_at',
        draft_bills: 'client_id, is_parked, created_at',
        local_quotes: 'client_id, server_id, status, is_synced, [tenant_id+location_id], created_at',
        sync_metadata: 'entity_type',
        app_config: 'key',
      })
      .upgrade(async (tx) => {
        const cfg = tx.table('app_config');
        const existing = await cfg.get(TENANT_CONFIG_KEY);
        if (!existing) {
          await cfg.put({
            key: TENANT_CONFIG_KEY,
            currency: DEFAULT_CURRENCY,
            tenant_id: null,
            updated_at: new Date().toISOString(),
          });
        }
      });

    // v7: products gain hide_in_pos / hide_in_sales (per-module visibility set
    // by the owner). Plain fields, filtered in JS — no new index, so the
    // schema string is unchanged; the version bump just versions the shape.
    this.version(7).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      local_quotes: 'client_id, server_id, status, is_synced, [tenant_id+location_id], created_at',
      sync_metadata: 'entity_type',
      app_config: 'key',
    });

    // v8 (T19): a synced BILL row stays 24 h as SYNCED with its server number
    // (server_id / server_bill_number / synced_at, plus a local customer_label).
    // Plain fields, no new index — the schema string is unchanged.
    this.version(8).stores({
      pending_sync: '++id, client_id, entity_type, status, created_at',
      products: 'id, sku, barcode, category, is_active, synced_at, [tenant_id+location_id], [tenant_id+location_id+barcode], [tenant_id+location_id+category]',
      customers: 'id, phone, email, synced_at',
      draft_bills: 'client_id, is_parked, created_at',
      local_quotes: 'client_id, server_id, status, is_synced, [tenant_id+location_id], created_at',
      sync_metadata: 'entity_type',
      app_config: 'key',
    });
  }
}

export const posDb = new PosDatabase();

/**
 * G2.27 — Purge all IndexedDB contents. Call this during key rotation
 * (logout / remote unenroll) after the in-memory key has been cleared.
 *
 * We delete + recreate the DB rather than clearing each table so that
 * any stale ciphertext bytes in IDB's WAL are also dropped.
 */
export async function wipeLocalDb(): Promise<void> {
  posDb.close();
  await Dexie.delete('bbpos');
  await posDb.open();
}

/**
 * Cache the tenant's immutable currency offline. Called after login / profile
 * fetch (see `use-auth-store`) so that an offline page reload still has the
 * correct currency for display and rounding. Idempotent upsert of the
 * singleton row; the value is normalized to a supported code (unknown →
 * `'INR'`), keeping every INR tenant byte-identical.
 */
export async function cacheTenantCurrency(
  currency: string | null | undefined,
  tenantId: string | null = null,
): Promise<void> {
  // Merge so sibling fields (tax_inclusive) written by other cachers survive.
  const existing = await posDb.app_config.get(TENANT_CONFIG_KEY);
  await posDb.app_config.put({
    ...existing,
    key: TENANT_CONFIG_KEY,
    currency: normalizeCurrency(currency),
    tenant_id: tenantId,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Cache the owner's "show prices inclusive of tax" display flag so an offline
 * reload renders the catalogue the same way. Merges into the singleton row.
 */
export async function cacheTenantTaxInclusive(value: boolean): Promise<void> {
  const existing = await posDb.app_config.get(TENANT_CONFIG_KEY);
  await posDb.app_config.put({
    key: TENANT_CONFIG_KEY,
    currency: existing?.currency ?? DEFAULT_CURRENCY,
    tenant_id: existing?.tenant_id ?? null,
    ...existing,
    tax_inclusive: value,
    updated_at: new Date().toISOString(),
  });
}

/**
 * Cache the signed-in user's discount cap. Merges into the singleton row, like
 * its siblings, so the currency and tax flag survive.
 */
export async function cacheDiscountCap(cap: { percent: number; flat: number | null } | null): Promise<void> {
  const existing = await posDb.app_config.get(TENANT_CONFIG_KEY);
  await posDb.app_config.put({
    key: TENANT_CONFIG_KEY,
    currency: existing?.currency ?? DEFAULT_CURRENCY,
    tenant_id: existing?.tenant_id ?? null,
    ...existing,
    discount_cap: cap ?? undefined,
    updated_at: new Date().toISOString(),
  });
}

/** Offline-cached discount cap; `null` when this till has never been served one. */
export async function getCachedDiscountCap(): Promise<{ percent: number; flat: number | null } | null> {
  const row = await posDb.app_config.get(TENANT_CONFIG_KEY);
  return row?.discount_cap ?? null;
}

/** Offline-cached display flag; `false` (tax-exclusive display) when unknown. */
export async function getCachedTenantTaxInclusive(): Promise<boolean> {
  const row = await posDb.app_config.get(TENANT_CONFIG_KEY);
  return row?.tax_inclusive === true;
}

/**
 * The offline-cached tenant currency. Defaults to `'INR'` when no config row
 * exists yet (fresh install before first sync, or a legacy value), so callers
 * — including the future subunit-aware offline rounding (task 290) — never
 * see `undefined`.
 */
export async function getCachedTenantCurrency(): Promise<CurrencyCode> {
  const row = await posDb.app_config.get(TENANT_CONFIG_KEY);
  return row ? normalizeCurrency(row.currency) : DEFAULT_CURRENCY;
}

/** Narrow an arbitrary IndexedDB row to detect residual plaintext fields. */
export function hasUnexpectedPlaintext(row: Record<string, unknown>, tableName: string): boolean {
  if (!isEncryptedTable(tableName)) return false;
  const allowed = new Set<string>([...PLAINTEXT_FIELDS[tableName], ENC_COLUMN]);
  for (const key of Object.keys(row)) {
    if (!allowed.has(key)) return true;
  }
  return false;
}
